import React, { useState } from "react";
import { getByPath } from "./logSources";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// LogEventsTable (#247) — presentational, source-agnostic log grid for the in-app
// Log viewer. Renders a compact table (column catalog per source) with click-to-
// expand rows that pretty-print the row's JSON detail. Self-contained: the Log
// viewer never depends on Compass.
//
// Props:
//   rows        — normalized rows (each carries __when + the column fields).
//   columns     — [{ label, path }] column catalog.
//   loading, error
//   levelColor  — (level) => css color, for the Level cell.
//   getDetail   — (row) => any: the object/text pretty-printed in the expand.
//   getRowKey   — (row, idx) => string.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function formatCell(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") {
    const keys = Object.keys(value);
    return `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
  }
  const s = String(value);
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
}

function LogEventsTable({
  rows,
  columns,
  loading,
  error,
  levelColor,
  getDetail,
  getRowKey,
}) {
  const [expanded, setExpanded] = useState({});

  if (error) {
    return (
      <div className="text-red text-sm p-4">
        {typeof error === "string" ? error : "Error loading logs."}
      </div>
    );
  }
  if (loading) {
    return <div className="text-skin-link text-sm p-4 italic">Loading logs…</div>;
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="text-skin-link text-sm p-4 italic">
        No log events match.
      </div>
    );
  }

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="overflow-auto h-full min-h-0">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr className="text-left sticky top-0 bg-skin-primary">
            <th className="px-2 py-1 border-b-2 border-yellow w-6" />
            {columns.map((c) => (
              <th
                key={c.path}
                className="px-2 py-1 border-b-2 border-yellow whitespace-nowrap font-medium"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const key = getRowKey ? getRowKey(row, idx) : String(idx);
            const isOpen = Boolean(expanded[key]);
            const detail = isOpen && getDetail ? getDetail(row) : null;
            return (
              <React.Fragment key={key}>
                <tr
                  onClick={() => toggle(key)}
                  className="cursor-pointer hover:bg-skin-base-hover border-b border-dark-grey"
                >
                  <td className="px-2 py-1 text-center align-top">
                    {isOpen ? "▼" : "▶"}
                  </td>
                  {columns.map((c) => {
                    const val = getByPath(row, c.path);
                    const isLevel = c.path === "level";
                    return (
                      <td
                        key={c.path}
                        className="px-2 py-1 whitespace-nowrap align-top"
                        style={
                          isLevel && levelColor
                            ? { color: levelColor(val), fontWeight: 600 }
                            : undefined
                        }
                        title={typeof val === "string" ? val : ""}
                      >
                        {formatCell(val)}
                      </td>
                    );
                  })}
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={columns.length + 1} className="bg-skin-base-hover p-2">
                      {detail && detail.json != null && (
                        <pre className="text-xs whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto border border-dark-grey rounded-sm p-2 bg-white text-black">
                          {JSON.stringify(detail.json, null, 2)}
                        </pre>
                      )}
                      {detail && detail.text != null && detail.text !== "" && (
                        <pre className="text-xs whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto border border-dark-grey rounded-sm p-2 mt-2 bg-white text-black">
                          {detail.text}
                        </pre>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default LogEventsTable;
